import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useAppKitAccount, useAppKit } from '@reown/appkit/react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { useAuthContext } from '@/context/AuthContext';
import { useReferral } from '@/hooks/useReferral';
import { usePreLaunchAccess } from '@/hooks/usePreLaunchAccess';
import {
  sanitizeInviteCodeInput,
  getInviteCodeError,
} from '@/utils/referralValidation';

export default function PreLaunch() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const { isConnected } = useAppKitAccount();
  const { open: openConnectModal } = useAppKit();
  const { bindCode, isBindingCode, hasInviter } = useReferral();
  const { countdownString, isKol } = usePreLaunchAccess();
  const queryClient = useQueryClient();

  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Prevent page scrolling
  useEffect(() => {
    // Save original overflow style
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalHeight = document.body.style.height;

    // Disable scrolling
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.height = '100vh';
    document.body.style.width = '100%';

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.height = originalHeight;
      document.body.style.width = '';
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const sanitized = sanitizeInviteCodeInput(rawValue);
    setInputValue(sanitized);
    setValidationError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isConnected || !isAuthenticated) {
      setValidationError('Please connect your wallet first');
      return;
    }

    const error = getInviteCodeError(inputValue);
    if (error) {
      setValidationError(error);
      return;
    }

    const codeToSubmit = inputValue.toUpperCase();

    bindCode(codeToSubmit, {
      onSuccess: async () => {
        setInputValue('');
        setValidationError(null);

        // Force refetch all access-related queries to get updated access state
        // Wait for all queries to complete before navigating
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['inviteInfo'] }),
          queryClient.refetchQueries({ queryKey: ['preLaunchStatus'] }),
          queryClient.refetchQueries({ queryKey: ['accessCheck'] }), // Critical: PreLaunchGuard uses this
        ]);

        navigate('/');
      },
      onError: (error: any) => {
        // If error is "already has invite code", the user already has access
        if (error?.message?.includes('already has invite code') || error?.message?.includes('30003')) {
          setInputValue('');
          // Refetch all queries to ensure UI updates correctly
          queryClient.refetchQueries({ queryKey: ['inviteInfo'] });
          queryClient.refetchQueries({ queryKey: ['preLaunchStatus'] });
          queryClient.refetchQueries({ queryKey: ['accessCheck'] });
          toast.success('You already have access!');
        } else {
          console.error('[PreLaunch] Unhandled error:', error);
          setValidationError(error?.message || 'Failed to verify code');
        }
      }
    });
  };

  return (
    <div className="flex items-center justify-center p-4" style={{ overflow: 'hidden', height: '100vh', width: '100vw', position: 'fixed', top: 0, left: 0 }}>
      {/* Disconnect Button - Fixed to top right of viewport */}
      {isConnected && (
        <button
          onClick={() => openConnectModal()}
          className="fixed top-4 right-4 z-50 px-4 py-2 text-sm font-medium transition-all hover:opacity-70"
          style={{
            fontFamily: 'Outfit, sans-serif',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            color: '#FFFFFF',
          }}
        >
          Disconnect
        </button>
      )}

      {/* Pre-launch video background */}
      <div className="fixed inset-0 -z-10 bg-black">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: 'brightness(0.5)',
          }}
        >
          <source src="/countdown.mp4" type="video/mp4" />
        </video>
      </div>

      <div className="relative w-full max-w-2xl">
        {/* Show simplified UI before wallet connection */}
        {!isConnected || !isAuthenticated ? (
          <div className="text-center py-16 space-y-8">
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <img
                src="/logo.svg"
                alt="Junk Fun"
                className="h-20 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>

            {/* Connect Wallet Text */}
            <p
              className="text-3xl md:text-4xl text-white mb-8"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 700,
              }}
            >
              Connect Wallet To Get Started
            </p>

            {/* Golden Connect Wallet Button */}
            <button
              onClick={() => openConnectModal()}
              className="px-8 py-4 font-bold text-lg transition-all hover:scale-105"
              style={{
                fontFamily: 'Hyperspace Race Capsule, cursive',
                borderRadius: '100px',
                background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                boxShadow: '0 8px 20px rgba(251, 139, 0, 0.4)',
                color: '#000000',
              }}
            >
              CONNECT WALLET
            </button>
          </div>
        ) : hasInviter ? (
          // User already has access - show success state
          <div className="text-center py-16 space-y-8">
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <img
                src="/logo.svg"
                alt="Junk Fun"
                className="h-20 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>

            {/* Success Message */}
            <div className="space-y-4">
              <div className="text-6xl mb-4">✅</div>
              <p
                className="text-3xl md:text-4xl text-white mb-4"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 700,
                }}
              >
                You Already Have Access!
              </p>
              <p
                className="text-lg md:text-xl text-white/80"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 400,
                }}
              >
                Your wallet is already linked to a referral code.
              </p>
            </div>

            {/* Go to Junk Page Button */}
            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 font-bold text-lg transition-all hover:scale-105"
              style={{
                fontFamily: 'Hyperspace Race Capsule, cursive',
                borderRadius: '100px',
                background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                boxShadow: '0 8px 20px rgba(251, 139, 0, 0.4)',
                color: '#000000',
              }}
            >
              GO TO JUNK PAGE
            </button>
          </div>
        ) : (
          <>
            {/* Logo Section */}
            <div className="text-center pt-10 pb-4">
              <div className="flex justify-center mb-2">
                <img
                  src="/logo.svg"
                  alt="Junk Fun"
                  className="h-16 w-auto"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </div>

            {/* Countdown Timer */}
            <div className="px-8 pb-6">
              <div className="text-center mb-6">
                <p
                  className="text-3xl md:text-4xl text-white mb-2"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  Start in {countdownString || 'Loading...'}
                </p>
              </div>

              {/* Prompt Text */}
              <div className="text-center mb-5">
                <p
                  className="text-lg md:text-xl mb-1 text-white"
                  style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600 }}
                >
                  Want to start immediately?
                </p>
                <p
                  className="text-base md:text-lg text-white/80"
                  style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400 }}
                >
                  Use an invitation code.
                </p>
              </div>

              {/* Invite Code Input Form */}
              <form onSubmit={handleSubmit} className="space-y-4 mb-6 max-w-sm mx-auto">
                <div>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder="Enter Code"
                    maxLength={5}
                    className={cn(
                      'w-full p-3 text-center text-lg font-mono uppercase',
                      'bg-white/95 border-0 rounded-xl',
                      'focus:outline-none focus:ring-2 focus:ring-[#50E4F9]/50',
                      'tracking-[0.3em]',
                      'text-black placeholder:text-gray-400',
                      validationError && 'ring-2 ring-red-500'
                    )}
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 600,
                    }}
                    disabled={isBindingCode}
                  />
                  {validationError && (
                    <p
                      className="text-red-400 text-xs mt-2 text-center"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {validationError}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isBindingCode || inputValue.length !== 5}
                  className={cn(
                    'w-full py-3 px-6 font-bold transition-all transform text-base uppercase',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    !isBindingCode && 'hover:scale-[1.02]'
                  )}
                  style={{
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                    borderRadius: '100px',
                    background:
                      'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                    boxShadow: '0 8px 20px rgba(251, 139, 0, 0.4)',
                    color: '#000000',
                  }}
                >
                  {isBindingCode ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="animate-spin" size={18} />
                      CONFIRMING...
                    </span>
                  ) : (
                    'CONFIRM'
                  )}
                </button>
              </form>

              {/* Bottom Section - How to Get Code */}
              <div className="pt-6 mt-4">
                <h3
                  className="text-center text-white mb-4 text-base font-semibold"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  How to Get an Invite Code
                </h3>
                <div className="space-y-2 mb-6 max-w-lg mx-auto">
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-white text-sm">1.</span>
                    <p
                      className="text-white/80 text-sm text-center"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      Get one from a friend who has invites.
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-white text-sm">2.</span>
                    <p
                      className="text-white/80 text-sm text-center"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      Join our official communities to get one
                    </p>
                  </div>
                </div>

                {/* Social Media Links */}
                <div className="flex items-center justify-center gap-6 pb-6">
                  <a
                    href="https://t.me/junkfunofficial"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-all hover:scale-110"
                    aria-label="Telegram"
                  >
                    <img
                      src="/ic_baseline-telegram.svg"
                      alt="Telegram"
                      className="w-10 h-10"
                    />
                  </a>
                  <a
                    href="https://x.com/junkfun_"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-all hover:scale-110"
                    aria-label="X (Twitter)"
                  >
                    <img
                      src="/prime_twitter.svg"
                      alt="X (Twitter)"
                      className="w-6 h-6"
                    />
                  </a>
                </div>
              </div>
            </div>

            {/* KOL Badge (if applicable) */}
            {isKol && (
              <div
                className="absolute top-6 right-6 px-5 py-2 rounded-full"
                style={{
                  background: 'rgba(254, 187, 1, 0.2)',
                  border: '2px solid #FEBB01',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <p
                  className="text-sm font-bold"
                  style={{
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                    color: '#FEBB01',
                  }}
                >
                  KOL ACCESS
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

